import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';

export default function Donate() {
  const { user } = useAuth();
  const [amount, setAmount] = useState<number | null>(50);
  const [customAmount, setCustomAmount] = useState('');
  const [frequency, setFrequency] = useState<'once' | 'monthly'>('once');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const presetAmounts = [25, 50, 100, 250, 500];

  const finalAmount = amount || (customAmount ? parseInt(customAmount) : 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('Please sign in to make a donation.');
      return;
    }
    if (finalAmount <= 0) {
      setError('Please select or enter a donation amount.');
      return;
    }

    setError('');
    setLoading(true);

    const { error: insertError } = await supabase.from('donations').insert({
      user_id: user.id,
      amount: finalAmount,
      currency: 'USD',
      frequency,
      status: 'completed',
      message: message || null,
    });

    setLoading(false);

    if (insertError) {
      setError(insertError.message);
    } else {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
    }
  };

  return (
    <div className="overflow-x-hidden">
      <section className="w-full pt-32 pb-20 md:pb-28 bg-warm-50">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
          <span className="text-brand-600 text-sm font-semibold tracking-wider uppercase mb-4 block">
            Make a Difference
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Your donation changes lives
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed">
            Every dollar goes directly to our programs. $25 feeds a family for a week.
            $100 sponsors a child school supplies for a year. Choose your impact.
          </p>
          {!user && (
            <div className="mt-6 bg-brand-50 border border-brand-200 rounded-xl p-4 inline-block">
              <p className="text-brand-800 text-sm">
                <Link to="/login" className="font-semibold underline">Sign in</Link> to track your donations, or continue as a guest.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="w-full py-20 md:py-28 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl mx-auto">
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-3-fill text-green-600 text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h3>
              <p className="text-gray-600 mb-2">Your generosity means the world to us.</p>
              <p className="text-gray-500 text-sm">
                Your donation has been recorded. You can view it on your <Link to="/profile" className="text-brand-600 font-semibold">profile page</Link>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="bg-red-50 text-red-600 text-sm p-4 rounded-xl">
                  {error}
                </div>
              )}

              {/* Frequency Toggle */}
              <div className="flex items-center justify-center gap-2 p-1.5 bg-gray-100 rounded-full max-w-md mx-auto">
                <button
                  type="button"
                  onClick={() => setFrequency('once')}
                  className={`flex-1 py-3 px-6 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    frequency === 'once'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  One-time
                </button>
                <button
                  type="button"
                  onClick={() => setFrequency('monthly')}
                  className={`flex-1 py-3 px-6 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    frequency === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Monthly
                </button>
              </div>

              {/* Amount Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                  Select amount
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {presetAmounts.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => {
                        setAmount(preset);
                        setCustomAmount('');
                      }}
                      className={`py-4 rounded-xl border-2 font-semibold text-lg transition-all whitespace-nowrap ${
                        amount === preset
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      ${preset}
                    </button>
                  ))}
                </div>
                <div className="mt-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-lg">$</span>
                    <input
                      type="number"
                      placeholder="Custom amount"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setAmount(null);
                      }}
                      className="w-full pl-10 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-brand-400 outline-none text-lg font-semibold"
                    />
                  </div>
                </div>
              </div>

              {/* Dedication Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dedication Message (optional)</label>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={200}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all"
                  placeholder="In honor of..."
                />
              </div>

              {/* Impact Statement */}
              {finalAmount > 0 && (
                <div className="bg-brand-50 rounded-xl p-5 text-center">
                  <p className="text-brand-800 font-medium">
                    Your {frequency === 'monthly' ? 'monthly' : ''} ${finalAmount} donation
                    {finalAmount >= 100 ? ' sponsors a child education for a semester' :
                     finalAmount >= 50 ? ' feeds a family for two weeks' :
                     finalAmount >= 25 ? ' provides emergency supplies for a disaster victim' :
                     ' helps us continue our mission'}
                  </p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || finalAmount <= 0}
                className="w-full py-5 bg-gray-900 text-white rounded-full font-bold text-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {loading ? 'Processing...' : frequency === 'monthly' ? 'Donate Monthly' : 'Donate Now'}
                <i className="ri-arrow-right-line"></i>
              </button>

              <p className="text-center text-gray-400 text-sm">
                Your donation is securely recorded in our system.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}